import { Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PaymentAdminDetailSchema } from "../../services/Schemas/Payment";
import { useProtectedAxiosInstance } from "../../hooks/axiosInstance";
import { useNotificationContext } from "../../context/notificationContext";
import { PaymentAdminDetailRequest } from "../../services/Endpoints/payments/PaymentAdDetailEndpoint";
import errorResolver from "../../utils/error-resolver";
import { dateFormatter } from "../../utils/date-formatter";
import InfoBadge, {
  OriginEnum,
} from "../../components/common/info-badge/InfoBadge";

export default function ReceiptsDetailPage() {
  const { id } = useParams();
  const [dataTransaction, setDataTransaction] = useState<
    PaymentAdminDetailSchema | undefined
  >(undefined);
  const navigate = useNavigate();
  const [waiting, setWaiting] = useState(false);

  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();

  const getDataTransaction = async () => {
    try {
      setWaiting(true);
      const walletListRequest = new PaymentAdminDetailRequest(
        protectedInstance
      );
      const resp = await walletListRequest.send(Number(id ?? 1));
      setDataTransaction(resp.data.payment_receipt);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataTransaction();
  }, []);

  return (
    <>
      <Stack
        width="100%"
        direction="column"
        justifyContent="start"
        alignItems="start"
        px={{ xs: "6px", md: "20px" }}
        gap="10px"
      >
        <Stack direction={"column"} width="100%">
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ md: "center", xs: "start" }}
            width="100%"
          >
            <Stack>
              <Typography
                variant="h4DemiBold"
                color={(theme) => theme.palette.dominant.main}
                sx={{
                  mb: "4px",
                  mt: { xs: "0px", sm: "24px" },
                }}
              >
                تراکنش ها
              </Typography>
              <Typography
                variant="footerDemiBold"
                sx={{
                  mb: "20px",
                  color: ({ palette }) => palette.complement[300],
                }}
              >
                جزئیات تراکنش
              </Typography>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={{ md: "space-between", xs: "center" }}
              width={{ xs: "100%", sm: "initial" }}
              gap="12px"
            ></Stack>
          </Stack>
        </Stack>
        <Box
          sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}
        >
          {/* <TabContext value={value}> */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}>
            {dataTransaction && (
              <Stack
                px="3px"
                py="6px"
                direction="row"
                justifyContent="start"
                alignItems="start"
                gap="20px"
                flexWrap="wrap"
              >
                <Stack
                  direction="row"
                  justifyContent="start"
                  alignItems="center"
                  gap="8px"
                  width="30%"
                >
                  <Typography variant="warningRegular">تاریخ ایجاد</Typography>
                  <Typography variant="captionMediumBold">
                    {dateFormatter({
                      lang: "fa",
                      date: dataTransaction.created_at,
                    })}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="start"
                  alignItems="center"
                  gap="8px"
                  width="30%"
                >
                  <Typography variant="warningRegular">وجوه</Typography>
                  <Typography variant="captionMediumBold">
                    {dataTransaction.amount}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="start"
                  alignItems="center"
                  gap="8px"
                  width="30%"
                >
                  <Typography variant="warningRegular">آیدی کاربر</Typography>
                  <Typography variant="captionMediumBold">
                    {dataTransaction.user_id}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="start"
                  alignItems="center"
                  gap="8px"
                  width="30%"
                >
                  <Typography variant="warningRegular"> لینک</Typography>
                  <Typography
                    variant="captionMediumBold"
                    component={Link}
                    to={dataTransaction.redirect_url}
                  >
                    {dataTransaction.redirect_url}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="start"
                  alignItems="center"
                  gap="8px"
                  width="30%"
                >
                  <Typography variant="warningRegular"> توضیحات</Typography>
                  <Typography variant="captionMediumBold">
                    {dataTransaction.description}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="start"
                  alignItems="center"
                  gap="8px"
                  width="30%"
                >
                  <Typography variant="warningRegular"> وضعیت</Typography>
                  <Typography variant="captionMediumBold">
                    <InfoBadge
                      origin={OriginEnum.PAYMENT}
                      status={dataTransaction.status as "pending"}
                    />
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Box>
          <Stack
            direction="column"
            justifyContent="start"
            mx="10px"
            my="10px"
            mt="40px"
            sx={{
              borderBottom: "1px solid",
              borderBottomColor: (theme) => theme.palette.gray[300],
            }}
          >
            <Typography variant="footerBold">لاگها</Typography>
          </Stack>
          <Stack gap="10px">
            {dataTransaction?.logs.map((log, i) => (
              <Stack
                key={log.created_at}
                px="10px"
                py="5px"
                sx={{
                  borderBottom: "1px solid",
                  borderBottomColor: (theme) => theme.palette.gray[300],
                  bgcolor: (theme) =>
                    i % 2 == 0
                      ? theme.palette.gray[200]
                      : theme.palette.gray[100],
                }}
              >
                <Stack direction="row" gap="20px" flexWrap="wrap">
                  <Stack
                    gap="20px"
                    direction="row"
                    width={{ xs: "100%", md: "30%" }}
                  >
                    <Typography variant="captionRegular"> تاریخ</Typography>
                    <Typography variant="captionExtraBold">
                      {dateFormatter({ lang: "fa", date: log.created_at })}
                    </Typography>
                  </Stack>
                  <Stack
                    gap="20px"
                    direction="row"
                    width={{ xs: "100%", md: "30%" }}
                  >
                    <Typography variant="captionRegular"> توضیحات</Typography>
                    <Typography variant="captionExtraBold">
                      {log.description}
                    </Typography>
                  </Stack>
                  <Stack
                    gap="20px"
                    direction="row"
                    width={{ xs: "100%", md: "30%" }}
                  >
                    <Typography variant="captionRegular"> عملیات</Typography>
                    <Typography variant="captionExtraBold">
                      {log.action}
                    </Typography>
                  </Stack>
                  <Stack
                    gap="20px"
                    direction="row"
                    width={{ xs: "100%", md: "30%" }}
                  >
                    <Typography variant="captionRegular">
                      {" "}
                      آیدی درگاه
                    </Typography>
                    <Typography variant="captionExtraBold">
                      {log.payment_receipt_id}
                    </Typography>
                  </Stack>
                  <Stack
                    gap="20px"
                    direction="row"
                    width={{ xs: "100%", md: "30%" }}
                  >
                    <Typography variant="captionRegular">
                      {" "}
                      آیدی کاربر
                    </Typography>
                    <Typography variant="captionExtraBold">
                      {log.user_id}
                    </Typography>
                  </Stack>
                  <Stack
                    gap="20px"
                    direction="row"
                    width={{ xs: "100%", md: "30%" }}
                  >
                    <Typography variant="captionRegular"> خصوصی</Typography>
                    <Typography variant="captionExtraBold">
                      {log.is_private}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Stack>
    </>
  );
}
